import React, { useEffect, useRef, useState } from "react";
import { Item, ComboBox } from "@adobe/react-spectrum";
import { useAsyncList } from "@react-stately/data";
import { useTranslation } from "react-i18next";
import { useDependency } from "../../../../../../contexts/DependencyProvider";
import { ListJournalRequest } from "../../../../../../services/soap/finance/requests/ListJournalRequest";
interface journal {
  id: string,
  name: string,
  isPrimary: string,
  type: string,
  numbering: string,
  numberingName: string
}
interface IJournalDropDownProps {
  selectedJournal: string;              // parent selected id
  onChange: (data: any | null) => void; // null means cleared
  isEdit: any;
  isRequired?: any;
  errorMessage?: any;
  validationState?: any;
}

const JournalDropDown: React.FC<IJournalDropDownProps> = ({
  selectedJournal,
  onChange,
  isEdit,
  isRequired,
  errorMessage,
  validationState,
}) => {
  const { store, financeService } = useDependency();
  const { t } = useTranslation("invoice");
  const _t = (key: string) => t(`add_update_invoice.${key}`, { ns: "invoice" });

  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState<string>("");

  const didInitialAutoSelectRef = useRef(false);
  const isUserEditingRef = useRef(false);

  const list = useAsyncList<journal>({
    async load({ filterText }) {
      try {
        const request = new ListJournalRequest(store.Server, store.SessionId);
        const response = await financeService.listJournal(request);
        console.log('journal response: ', response);
        let items: journal[] = response?.JOURNAL || [];
        if (!Array.isArray(items)) return { items: [] };

        const ft = (filterText ?? "").trim().toLowerCase();
        if (ft) {
          items = items.filter((x) => (x.name ?? "").toLowerCase().includes(ft));
        }

        return { items };
      } catch (e) {
        console.error("Error fetching ItemStatus:", e);
        return { items: [] };
      }
    },
  });

  useEffect(() => {
    list.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ✅ initial load: auto-select 0th item ONCE
  useEffect(() => {
    if (!didInitialAutoSelectRef.current && list.items.length > 0 && !isEdit) {
      didInitialAutoSelectRef.current = true;

      const first = list.items[0];
      const firstId = String((first as any).id);

      setSelectedKey(firstId);
      setInputValue(first.name ?? "");
      onChange(first);
    }
  }, [list.items, isEdit, onChange]);

  // ✅ sync from parent ONLY when user is not actively typing
  useEffect(() => {
    if (isUserEditingRef.current) return;

    if (!selectedJournal) {
      setSelectedKey(null);
      setInputValue("");
      return;
    }

    setSelectedKey(String(selectedJournal));
    const selected = list.items.find((x) => String((x as any).id) === String(selectedJournal));
    setInputValue(selected?.name ?? "");
  }, [selectedJournal, list.items]);

  const handleInputChange = (val: string) => {
    isUserEditingRef.current = true;

    setInputValue(val);
    list.setFilterText(val);

    // ✅ IMPORTANT: allow deleting text even if something is selected
    // If user input diverges from selected option label, clear the selectedKey
    if (selectedKey) {
      const selected = list.items.find((x) => String((x as any).id) === String(selectedKey));
      const selectedLabel = (selected?.name ?? "");
      if (val !== selectedLabel) {
        setSelectedKey(null);
      }
    }

    // ✅ goal #4: when cleared -> empty selection
    if (!val.trim()) {
      setSelectedKey(null);
      list.setFilterText(""); // reset filtering
      onChange(null);         // tell parent it is cleared
    }
  };

  const handleSelectionChange = (key: React.Key | null) => {
    isUserEditingRef.current = false;

    const idStr = key ? String(key) : null;
    setSelectedKey(idStr);

    if (!idStr) return;

    const selected = list.items.find((x) => String((x as any).id) === idStr);
    if (selected) {
      setInputValue(selected.name ?? ""); // show immediately
      onChange(selected);

      // clear filter so next focus shows all values
      list.setFilterText("");
    }
  };

  return (
    <ComboBox
      label={_t("journal")}
      aria-label={_t("journal")}
      items={list.items}
      selectedKey={selectedKey}
      inputValue={inputValue}
      loadingState={list.loadingState}
      width={{ base: "100%", L: "100%" }}
      isDisabled={isEdit}
      isRequired={isRequired}
      errorMessage={errorMessage}
      validationState={validationState}
      onFocus={() => {
        // ✅ goal #2 show all values when user clicks in
        isUserEditingRef.current = true;
        list.setFilterText("");
      }}
      onBlur={() => {
        isUserEditingRef.current = false;
      }}
      onInputChange={handleInputChange}
      onSelectionChange={handleSelectionChange}
    >
      {(item) => (
        <Item key={(item as any).id} textValue={item.name}>
          {item.name}
        </Item>
      )}
    </ComboBox>
  );
};

export default JournalDropDown;
